import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { IconInfo, IconSmallArrow, IconStar, OrangeButton, SmallButton } from 'components';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROLE_TYPES } from 'api/constants';
import { endpoints } from 'api/endpoints';
import { clientSubmitFeedback, getRequestData } from 'api/api';
import { Form, Radio, Input, Rate } from 'antd';
import { getIdFromPath } from 'utils/helpers';

export const WeeklyRegularForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [taskData, setTaskData] = useState();
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(window.innerWidth > 900);
  const [form] = Form.useForm();
  const weekId = useMemo(() => {
    return getIdFromPath(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    weekId &&
      (async () => {
        const { data } = await getRequestData(
          endpoints.getClientFormsByWeek(weekId),
          ROLE_TYPES.client,
        );
        if (data) setTaskData(data);
      })();
  }, [weekId]);

  const isWeeklyTaskCompleted = taskData && taskData[0]?.completed;

  const storeFields = useMemo(() => {
    return (
      taskData &&
      taskData[0]?.forms[0]?.feedback.map((formItem, index) => {
        return (
          <Fragment key={index}>
            <div className="flex flex-col px-6 md:px-4 py-4 bg-white rounded-block-radius-sm md:rounded-2xl gap-y-4 md:gap-y-3 shadow-block-shadow">
              <div className="flex items-start gap-x-2">
                <b className="nunito text-orange italic text-18 md:text-16">{index + 1}.</b>
                <p className="font-medium text-16 md:text-14">{formItem.question}</p>
              </div>
              <div className="flex flex-row items-center gap-x-4 md:flex-col md:items-start md:gap-2">
                {formItem.type === 'rating' && (
                  <p className="text-14 md:text-12 font-medium text-lightBlue flex md:hidden">
                    Strongly
                    <br /> Disagree
                  </p>
                )}
                <Form.Item
                  className={`${formItem.type === 'rating' ? 'w-auto md:w-full' : 'w-full flex-1'}`}
                  name={formItem.feedback_id}
                  rules={[{ required: formItem.mandatory, message: 'This field is required' }]}
                >
                  {formItem.type === 'rating' ? (
                    <Rate
                      disabled={isWeeklyTaskCompleted}
                      name={`input-${formItem.feedback_id}`}
                      character={({ index = 0 }) => {
                        return (
                          <div className="rate flex flex-col items-center justify-center rounded-xl border border-color-divider py-2 bg-white w-[64px] h-[64px] md:w-[52px] md:h-[52px] gap-y-1 md:gap-y-0.5">
                            <IconStar className="w-7 h-7 md:w-6 md:h-6" />
                            <span className="text-15 md:text-13 text-black rate-text">{index + 1}</span>
                          </div>
                        );
                      }}
                    />
                  ) : formItem.type === 'radio' ? (
                    <Radio.Group className="flex flex-wrap gap-2" name={`input-${formItem.feedback_id}`}>
                      {formItem.items.map((item) => {
                        return (
                          <Radio
                            disabled={isWeeklyTaskCompleted}
                            key={`radio-${item.feedback_item_id}`}
                            className="px-4 py-2 bg-white border border-color-divider rounded-xl !mr-0 text-14 md:text-13 font-medium"
                            value={item.feedback_item_id}
                          >
                            {item.feedback_item_label}
                          </Radio>
                        );
                      })}
                    </Radio.Group>
                  ) : (
                    <Input.TextArea
                      readOnly={isWeeklyTaskCompleted}
                      className="!resize-none font-poppins text-14 p-3 pl-4 border border-color-divider rounded-ten max-h-[100px] w-full"
                      autoSize={false}
                      name={`input-${formItem.feedback_id}`}
                      rows={4}
                      placeholder="Type your answer..."
                    />
                  )}
                </Form.Item>
                {formItem.type === 'rating' && (
                  <p className="text-14 md:text-12 font-medium text-lightBlue flex md:hidden">
                    Strongly
                    <br /> Agree
                  </p>
                )}
              </div>
            </div>
          </Fragment>
        );
      })
    );
  }, [isWeeklyTaskCompleted, taskData]);

  const initialValues = useMemo(() => {
    if (!taskData) return {};

    const feedbackObject = taskData[0]?.forms[0].feedback?.reduce((acc, item) => {
      if (item.items.length > 1) {
        const selectedItem =
          item.items.find(
            (feedbackItem) =>
              feedbackItem.answer !== null &&
              feedbackItem.answer !== undefined &&
              (feedbackItem.answer === String(feedbackItem.feedback_item_id) ||
                feedbackItem.answer === String(feedbackItem.feedback_item_label) ||
                feedbackItem.answer === String(item.items.indexOf(feedbackItem) + 1)),
          ) || item.items.find((fi) => fi.answer !== null && fi.answer !== undefined);

        if (selectedItem) {
          if (item.type === 'rating') {
            const index = item.items.indexOf(selectedItem);
            const ansNum = Number(selectedItem.answer);
            acc[item.feedback_id] =
              !isNaN(ansNum) && ansNum >= 1 && ansNum <= 5
                ? ansNum
                : index >= 0
                ? index + 1
                : undefined;
          } else {
            acc[item.feedback_id] = selectedItem.feedback_item_id;
          }
        } else if (item.type === 'radio') {
          acc[item.feedback_id] = item.items[0]?.feedback_item_id;
        }
      } else {
        const val = item.items[0]?.answer;
        if (item.type === 'rating') {
          const num = Number(val);
          acc[item.feedback_id] = !isNaN(num) && num > 0 ? num : undefined;
        } else {
          acc[item.feedback_id] = val || undefined;
        }
      }

      return acc;
    }, {});

    return feedbackObject;
  }, [taskData]);

  const handleFormSubmit = (values) => {
    const data = Object.entries(values).map(([key, value]) => {
      const questionData = taskData[0]?.forms[0].feedback.find(
        (f) => f.feedback_id.toString() === key,
      );
      let selectedItem;

      if (questionData.items.length > 1) {
        selectedItem = questionData.items.find(
          (item) =>
            item.feedback_item_id.toString() === (item?.answer ? item.answer.toString() : '') ||
            item.feedback_item_id.toString() === (value ? value.toString() : '') ||
            item?.feedback_item_label.toString() === (item?.answer ? item.answer.toString() : '') ||
            item?.feedback_item_label.toString() === (value ? value.toString() : ''),
        );
      } else {
        selectedItem = questionData.items[0];
      }

      return {
        form_id: taskData[0].forms[0].form_id,
        feedback_id: Number(key),
        feedback_item_id: selectedItem ? selectedItem.feedback_item_id : questionData.items[0].feedback_item_id,
        task_id: taskData[0].task_id,
        answer: value ? value.toString() : null,
      };
    });

    clientSubmitFeedback(data, ROLE_TYPES.client, () => {
      navigate('/dashboard');
    });
  };

  return (
    taskData?.length > 0 && (
      <div className="flex flex-col flex-1 gap-y-8 md:gap-y-4 w-full max-w-full">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-x-4 md:gap-x-3">
            <div
              onClick={() => navigate(-1)}
              className="min-w-10 h-10 md:min-w-9 md:h-9 bg-white rounded-lg md:rounded-full shadow-block-shadow flex items-center justify-center cursor-pointer"
            >
              <IconSmallArrow className="w-2 h-4" />
            </div>
            <p className="nunito-title text-22 md:text-16">{taskData[0]?.forms[0].name}</p>
          </div>
        </div>
        <Form
          form={form}
          initialValues={initialValues}
          onFinish={handleFormSubmit}
          className="flex flex-col gap-y-4 client-forms w-full"
        >
          {taskData && !isWeeklyTaskCompleted && (
            <div className="flex flex-col p-5 md:p-4 border border-blue rounded-[30px] md:rounded-2xl gap-y-2 bg-white">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setIsInstructionsOpen((prev) => !prev)}
              >
                <div className="flex items-center gap-x-2">
                  <IconInfo color="blue" />
                  <p className="text-base md:text-14 font-medium text-blue">Weekly Check-In Instructions</p>
                </div>
                <svg
                  className={`w-3.5 h-3.5 transform transition-transform duration-200 ${isInstructionsOpen ? 'rotate-180' : ''}`}
                  viewBox="0 0 12 7"
                  fill="none"
                >
                  <path d="M1 1L6 6L11 1" stroke="#4318FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              {isInstructionsOpen && (
                <div className="flex flex-col mt-2 pt-2 border-t border-divider">
                  <p className="text-14 text-black mt-1 mb-2">
                    Filling out this survey each week is part of your Sober Living Scholarship. Here’s why it matters:
                  </p>
                  <div className="flex flex-col gap-y-1 pl-2">
                    <p className="text-14 text-black mt-1">
                      &bull; Shows your commitment to recovery and your scholarship.
                    </p>
                    <p className="text-14 text-black mt-1">
                      &bull; Gives you a chance to notice your wins, challenges, and growth.
                    </p>
                    <p className="text-14 text-black mt-1">
                      &bull; Helps us stay connected and offer support when needed.
                    </p>
                    <p className="text-14 text-black mt-1">
                      &bull; Shapes Power Forward’s work and strengthens recovery for everyone.
                    </p>
                    <p className="text-14 text-black mt-1">
                      &bull; Your responses are private—we track program results without using names and never share personal details.
                    </p>
                  </div>
                  <div className="flex flex-col mt-3">
                    <div className="flex items-center gap-x-1">
                      <IconInfo color="blue" />
                      <p className="text-base font-medium text-blue">How to Use the Star Ratings</p>
                    </div>
                    <div className="flex flex-col">
                      <p className="flex items-center text-14 text-black mt-1">
                        1 <IconStar className="w-3.5 h-3.5 flex-shrink-0 mx-1 ml-2 inline-block" /> - Barely did it or almost no impact; very little effect
                      </p>
                      <p className="flex items-center text-14 text-black mt-1">
                        2 <IconStar className="w-3.5 h-3.5 flex-shrink-0 mx-1 inline-block" /> - Did it somewhat; some impact, but not much
                      </p>
                      <p className="flex items-center text-14 text-black mt-1">
                        3 <IconStar className="w-3.5 h-3.5 flex-shrink-0 mx-1 inline-block" /> - Neutral; no noticeable change
                      </p>
                      <p className="flex items-center text-14 text-black mt-1">
                        4 <IconStar className="w-3.5 h-3.5 flex-shrink-0 mx-1 inline-block" /> - Did it more than before; moderate impact; noticeable change
                      </p>
                      <p className="flex items-center text-14 text-black mt-1">
                        5 <IconStar className="w-3.5 h-3.5 flex-shrink-0 mx-1 inline-block" /> - Did it a lot; excellent progress; a lot of impact; very positive change
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {storeFields}
          {!isWeeklyTaskCompleted && (
            <div className="flex flex-row md:flex-col-reverse self-end md:self-stretch gap-x-4 md:gap-3 mt-2 md:mt-4">
              <SmallButton label="Cancel" href="/dashboard" btnClassName="!h-[44px] md:w-full font-semibold text-15 rounded-xl" />
              <OrangeButton
                type="submit"
                label="Send"
                btnClassName="ant-btn-green font-semibold text-15 !h-[44px] rounded-xl px-8 md:w-full"
              />
            </div>
          )}
        </Form>
      </div>
    )
  );
};
